import { useEffect, useState } from "react";
import Card from "../../../../ui/Card";
import Button from "../../../../ui/Button";
import { getPageContent } from "../../../../api/publicContent.api";
import { getAdminContentBySection } from "../../../../api/adminContent.api";
import ContentEditor from "./ContentEditor";
import { MdEdit, MdExpandMore } from "react-icons/md";
import { resolveMediaUrl } from "../../../../utils/media";

const LANGS = ["en", "ar", "ku"];

const PAGES = [
  { key: "mhero1", label: "Mhero1" },
  { key: "mhero2", label: "Mhero2" },
  { key: "voyahfree", label: "Voyahfree" },
  { key: "voyahdream", label: "Voyahdream" },
  { key: "voyahtaishan", label: "Voyah Taishan" },
];

const SECTIONS_BY_PAGE = {
  mhero1: [
    "hero",
    "stats",
    "warranty",
    "technical_specs",
    "gallery",
    "features",
    "extras",
    "feature_tabs",
  ],
  mhero2: [
    "hero",
    "stats",
    "warranty",
    "technical_specs",
    "gallery",
    "features",
    "extras",
    "feature_tabs",
  ],
  voyahfree: [
    "hero",
    "warranty",
    "technical_specs",
    "gallery",
    "features",
    "extras",
  ],
  voyahdream: [
    "hero",
    "warranty",
    "technical_specs",
    "gallery",
    "features",
    "extras",
  ],
  voyahtaishan: [
    "hero",
    "warranty",
    "technical_specs",
    "gallery",
    "features",
    "extras",
    "feature_tabs",
  ],
};

const isVideo = (path) =>
  typeof path === "string" &&
  (path.endsWith(".mp4") || path.endsWith(".webm"));

const titleize = (str = "") =>
  str.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const ContentPage = () => {
  const [selectedPage, setSelectedPage] = useState("mhero1");
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openSections, setOpenSections] = useState({});
  const [adminCache, setAdminCache] = useState({});

  const load = async () => {
    setLoading(true);

    const responses = await Promise.all(
      LANGS.map((l) => getPageContent(selectedPage, l))
    );

    const merged = {};
    LANGS.forEach((l, i) => {
      merged[l] = responses[i]?.sections || {};
    });

    setContent(merged);
    setLoading(false);
  };

  useEffect(() => {
    load();
    setOpenSections({});
    setAdminCache({});
  }, [selectedPage]);

  const openEditor = async (section) => {
    let items = adminCache[section];

    if (!items) {
      items = await getAdminContentBySection(selectedPage, section);
      setAdminCache((p) => ({ ...p, [section]: items }));
    }

    setEditing({ page: selectedPage, section, items });
  };

  const toggleSection = (section) => {
    setOpenSections((p) => ({ ...p, [section]: !p[section] }));
  };

  const renderValue = (key, value) => {
    if (!value) return null;

    // Gallery / slider (array)
    if (Array.isArray(value)) {
      return (
        <div key={key} className="grid grid-cols-2 gap-2">
          {value.map((v, i) => {
            const src = resolveMediaUrl(v);
            return isVideo(v) ? (
              <video
                key={i}
                src={src}
                controls
                className="rounded-md"
              />
            ) : (
              <img
                key={i}
                src={src}
                alt=""
                className="rounded-md"
              />
            );
          })}
        </div>
      );
    }

    // Single media
    if (typeof value === "string" && value.startsWith("/uploads")) {
      const src = resolveMediaUrl(value);
      return isVideo(value) ? (
        <video
          key={key}
          src={src}
          controls
          className="rounded-md"
        />
      ) : (
        <img
          key={key}
          src={src}
          alt=""
          className="rounded-md"
        />
      );
    }

    // Plain text
    return (
      <p key={key} className="text-sm">
        <span className="font-semibold">{titleize(key)}:</span>{" "}
        {value}
      </p>
    );
  };

  return (
    <>
      <div className="space-y-6">
        <Card title="Vehicle Pages">
          <div className="flex gap-2 flex-wrap">
            {PAGES.map((p) => (
              <button
                key={p.key}
                onClick={() => setSelectedPage(p.key)}
                className={`px-4 py-2 rounded-lg ${
                  selectedPage === p.key
                    ? "bg-[#56a5a9] text-white"
                    : "bg-gray-100"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </Card>

        {SECTIONS_BY_PAGE[selectedPage]?.map((section) => (
          <Card key={section}>
            <div
              className="flex justify-between cursor-pointer"
              onClick={() => toggleSection(section)}
            >
              <h3 className="text-lg font-semibold">
                {titleize(section)}
              </h3>
              <MdExpandMore
                size={24}
                className={openSections[section] ? "rotate-180" : ""}
              />
            </div>

            {openSections[section] && !loading && (
              <div className="pt-4 space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  {LANGS.map((lang) => (
                    <div
                      key={lang}
                      className="border rounded-lg p-3 space-y-2"
                    >
                      <div className="text-xs font-bold uppercase text-gray-400">
                        {lang}
                      </div>

                      {content[lang]?.[section]
                        ? Object.entries(
                            content[lang][section]
                          ).map(([k, v]) =>
                            renderValue(k, v)
                          )
                        : "No content"}
                    </div>
                  ))}
                </div>

                <Button
                  variant="outline"
                  onClick={() => openEditor(section)}
                >
                  <MdEdit className="mr-2" /> Edit Section
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white max-w-4xl w-full max-h-[85vh] overflow-y-auto p-5 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">
              Edit {editing.page} / {editing.section}
            </h2>

            <ContentEditor
              items={editing.items}
              onSaved={() => {
                setEditing(null);
                setAdminCache({});
                load();
              }}
            />

            <div className="flex justify-end mt-6">
              <Button onClick={() => setEditing(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContentPage;
